import type { CSSProperties } from 'react'

import { cn } from '@/lib/utils'

interface PhotoProps {
    src: string
    alt: string
    rotation?: number
    fit?: 'cover' | 'contain'
    position?: string
    eager?: boolean
    as?: 'div' | 'span'
    className?: string
    imageClassName?: string
}

/**
 * An image that fills its box and can be turned in quarter steps, for phone
 * photos that were saved sideways. The box must get its size from its parent
 * (an aspect ratio, a height, or absolute positioning), never from the image.
 */
export default function Photo({
    src,
    alt,
    rotation = 0,
    fit = 'cover',
    position,
    eager = false,
    as: Wrapper = 'div',
    className,
    imageClassName
}: PhotoProps) {
    const quarter = (((Math.round(rotation / 90)) % 4) + 4) % 4
    const sideways = quarter === 1 || quarter === 3

    const imageStyle: CSSProperties = {
        width: sideways ? '100cqh' : '100%',
        height: sideways ? '100cqw' : '100%',
        transform: `translate(-50%, -50%) rotate(${quarter * 90}deg)`,
        objectPosition: position
    }

    return (
        <Wrapper
            className={cn('relative block overflow-hidden', className)}
            style={sideways ? { containerType: 'size' } : undefined}
        >
            <img
                src={src}
                alt={alt}
                loading={eager ? 'eager' : 'lazy'}
                decoding='async'
                draggable={false}
                className={cn(
                    'absolute top-1/2 left-1/2 max-w-none select-none',
                    fit === 'cover' ? 'object-cover' : 'object-contain',
                    imageClassName
                )}
                style={imageStyle}
            />
        </Wrapper>
    )
}
